import { ProgramWizardInActiveWarning } from './program-wizard/common';
import {
  getAvailableLookupsForVersionAndSubcode,
  getDeprecatedLookupsForProgram,
  ProgramWizardLookupsManager,
} from './services';
import {
  prepareCustomOutputFields,
  prepareStandardOutputFields,
} from './program-wizard/common/output-layout-service';
import {
  buildFileNamesTemplate,
  generateTimeBasedKey,
  getVersionFromDate,
} from '../common';
import {
  ProgramTransformer,
  ProgramTransformerArgs,
  ProgramWizardMessageId,
  ProgramWizardMetadata,
} from '@/app/core/components/program-wizard';
import { objectUtils } from '@/app/core';
import {
  asContact,
  ClientAttributes,
  DataNotFoundError,
  DeliveryFileInfo,
  EmptyResponse,
  fetchAndEnforceApiResponseData,
  FieldDelimiter,
  getEmptyAvailableAscendQuestLookups,
  getEmptyDeprecatedAscendQuestLookups,
  ProgramDataType,
  SSARProgram,
} from '@ssar-app/domain';
import { Center, VStack } from '@chakra-ui/react';
import { Modal, Spin } from '@ssar-app/uikit';

export const removeOrderNameForCloneProgramFlow: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  const { program, programWizardMetadata } = args;
  const { isDraft } = programWizardMetadata;
  const newProgram = program as SSARProgram;
  if (newProgram && newProgram.orderDetails && !isDraft) {
    newProgram.orderDetails.name = '';
  }
  return Promise.resolve({
    ...args,
    program,
  });
};

const displayModal = () => {
  return Modal.info({
    icon: null,
    contentProps: { w: 96 },
    closable: false,
    showOk: false,
    footer: <></>,
    closeOnEsc: false,
    closeOnOverlayClick: false,
    content: (
      <VStack>
        <Spin spinning={true} tip=''>
          <Center mt={24}>Loading required data. Please wait...</Center>
        </Spin>
      </VStack>
    ),
  });
};

export const prepareProgramAndDependentData: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  const { program, programWizardMetadata, programWizardDependentData } = args;
  // prepare the program
  const actualProgram = prepareProgram(
    program as SSARProgram,
    programWizardMetadata,
    programWizardDependentData,
  );

  // load dependent data
  return await loadDependentData(args, actualProgram);
};

const loadDependentData = async (
  args: ProgramTransformerArgs,
  actualProgram: SSARProgram,
): Promise<ProgramTransformerArgs> => {
  const { programWizardMetadata, messagesStore, programWizardDependentData } =
    args;

  // load the dependent data
  const programId = programWizardMetadata.from || '';
  return new Promise((resolve, reject) => {
    const modal = displayModal();
    getVersionFromDate(Date.now()).then(async (res) => {
      const version = res.data as string;
      actualProgram.dataDictionaryVersion = version;
      Promise.all([
        fetchAndEnforceApiResponseData(() =>
          getAvailableLookupsForVersionAndSubcode(
            version,
            actualProgram?.subCode || '',
          ),
        ),
        programId
          ? fetchAndEnforceApiResponseData(() =>
              getDeprecatedLookupsForProgram(programId),
            )
          : Promise.resolve(getEmptyDeprecatedAscendQuestLookups()),
        actualProgram?.outputLayout?.isCustomized
          ? prepareCustomOutputFields(actualProgram)
          : prepareStandardOutputFields(actualProgram, false),
      ])
        .then(async ([availableLookups, deprecatedLookups, outoutProgram]) => {
          const instance = new ProgramWizardLookupsManager(
            availableLookups || getEmptyAvailableAscendQuestLookups(),
            deprecatedLookups || getEmptyDeprecatedAscendQuestLookups(),
          );
          if (instance.doesLookupsExist()) {
            if (instance.doesDisabledLookupsExist()) {
              messagesStore.addMessage({
                id: ProgramWizardMessageId.DeprecatedLookupsExist,
                type: 'warning',
                message: (
                  <ProgramWizardInActiveWarning
                    lookupsManager={instance}
                  ></ProgramWizardInActiveWarning>
                ),
              });
            }
            resolve({
              ...args,
              program: {
                ...actualProgram,
                dataDictionaryVersion: version,
                outputLayout: { ...outoutProgram.outputLayout },
              },
              messagesStore: messagesStore,
              programWizardDependentData: {
                ...programWizardDependentData,
                lookupsManager: instance,
              },
            });
          } else {
            throw new DataNotFoundError(
              'Error occurred while loading data, please try again.',
            );
          }
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          modal.destroy();
        });
    });
  });
};

const excludeInputFile = (programData: ProgramDataType) => {
  const ssarProgram = programData as SSARProgram;
  const toReturn = {
    ...ssarProgram,
    inputFile: {
      ...ssarProgram.inputFile,
      clientFileName: '',
      fileName: '',
    },
  };
  return toReturn;
};

const buildDeliveryFileInfo = (
  fileName: string,
  sourceDeliveryFileInfo?: DeliveryFileInfo,
): DeliveryFileInfo | undefined => {
  if (sourceDeliveryFileInfo) {
    return {
      ...sourceDeliveryFileInfo,
      name: fileName,
    };
  }
  return undefined;
};

const prepareProgram = (
  program: Partial<SSARProgram> | null,
  metadata: ProgramWizardMetadata,
  dependentData: EmptyResponse,
): SSARProgram => {
  const ssarProgram = program as SSARProgram;
  const { orderDetails, outputLocations, outputLayout, ...restSSARProgram } =
    ssarProgram || {};
  const clientAttributes: ClientAttributes =
    dependentData.clientAttributesModel.clientAttributes;
  const {
    contact,
    tmsNumber: _tmsNumber,
    doNotProcessDupsFlag: _doNotProcessDupsFlag,
    noPinPctMax: _noPinPctMax,
    listConvertErrorPctMax: _listConvertErrorPctMax,
    ...restOrderDetails
  } = orderDetails || {};

  const clientContacts = clientAttributes?.contacts
    .filter((contact) => {
      if (contact.recordType.toLowerCase() === 'client') return true;
    })
    .map((contact) => asContact(contact));

  const thirdPartyContacts = clientAttributes?.contacts
    .filter((contact) => {
      if (contact.recordType.toLowerCase() === 'thirdparty') return true;
    })
    .map((contact) => asContact(contact));

  const shippingAddressContacts = clientAttributes?.contacts
    .filter((contact) => {
      if (
        contact.recordType?.toLowerCase() === 'client' &&
        contact.addressType?.toLowerCase() === 'shipping'
      )
        return true;
    })
    .map((contact) => asContact(contact));

  const isClientContactAvailable =
    clientContacts?.filter((contactToSearch) => {
      if (objectUtils.areEqual(contact, contactToSearch, '_id')) {
        return true;
      }
    }).length !== 0;

  const newProgramKey = generateTimeBasedKey();
  const newOutputLocations = (outputLocations || []).map((outputLocation) => {
    const { contact, ...restOutputLocation } = outputLocation;
    const isOutputContactAvailable =
      thirdPartyContacts?.filter((contactToSearch) => {
        if (objectUtils.areEqual(contact, contactToSearch, '_id')) {
          return true;
        }
      }).length !== 0 ||
      shippingAddressContacts?.filter((contactToSearch) => {
        if (objectUtils.areEqual(contact, contactToSearch, '_id')) {
          return true;
        }
      }).length !== 0;

    if (metadata?.isClone) {
      const newFileNames = buildFileNamesTemplate(newProgramKey);
      return {
        ...restOutputLocation,
        contact: isOutputContactAvailable ? contact : undefined,
        mainFile: buildDeliveryFileInfo(
          newFileNames.mainFileName,
          outputLocation?.mainFile,
        ),
        rejectFile: buildDeliveryFileInfo(
          newFileNames.rejectFileName,
          outputLocation?.rejectFile,
        ),
        statementFile: buildDeliveryFileInfo(
          newFileNames.statementFileName,
          outputLocation?.statementFile,
        ),
        zippedFile: buildDeliveryFileInfo(
          newFileNames.zippedFileName,
          outputLocation?.zippedFile,
        ),
      };
    } else {
      return {
        ...restOutputLocation,
        contact: isOutputContactAvailable ? contact : undefined,
      };
    }
  });

  let newProgram: SSARProgram = {
    ...restSSARProgram,
    subCode:
      program?.subCode || dependentData.clientAttributesModel.subscriberCode,
    programKey: newProgramKey,
    orderDetails: {
      contact: isClientContactAvailable ? contact : undefined,
      tmsNumber: clientAttributes?.details?.accountNumber || '',
      doNotProcessDupsFlag: clientAttributes?.details.doNotProcessDupsFlag,
      noPinPctMax: clientAttributes?.details.noPinPctMax,
      listConvertErrorPctMax: clientAttributes?.details.noPinPctMax,
      ...restOrderDetails,
    },
    scoresAndAddonProducts: {
      scores: ssarProgram?.scoresAndAddonProducts?.scores || [],
      addonProducts: ssarProgram?.scoresAndAddonProducts?.addonProducts || [],
    },
    attributesAndBundles: {
      attributes: ssarProgram?.attributesAndBundles?.attributes || [],
      bundles: ssarProgram?.attributesAndBundles?.bundles || [],
      fullAttributes: ssarProgram?.attributesAndBundles?.fullAttributes || [],
    },
    reports: {
      distribution: {
        attributes: ssarProgram?.reports?.distribution?.attributes || [],
      },
    },
    outputLocations: newOutputLocations,
    outputLayout: {
      isCustomized: outputLayout?.isCustomized || false,
      fields: outputLayout?.fields || [],
      delimiter: outputLayout?.delimiter || FieldDelimiter.COMMA,
    },
  };

  if (metadata?.isClone && !metadata.isDraft) {
    newProgram = excludeInputFile(newProgram);
  }
  return newProgram;
};
